import React, { useEffect, useState } from "react";
import { Share2, Eye, KeyRound, LogOut, X, Wrench, Megaphone, Smartphone, MessageSquare, Sparkles, Trophy, AlertTriangle, Palette, Inbox, Volume2, Layers, Coffee, Heart, Star, Activity } from "lucide-react";
import { TROPHIES } from "@/lib/trophies";
import { OwnerFeaturedManager } from "@/components/OwnerFeaturedManager";
import { OwnerStats } from "@/components/OwnerStats";
import { broadcastThemeUpdated } from "@/lib/theme";
import OwnerInboxPanel, { useUnreadInbox } from "@/components/OwnerInbox";
import TributePanel from "@/components/TributePanel";
import OwnerAudioPanel from "@/components/OwnerAudio";
import OwnerCategoriesPanel from "@/components/OwnerCategoriesPanel";
import { WELCOME_TOKENS, interpolateWelcome } from "@/lib/welcomeTokens";

export default function OwnerBar({
  isOwner,
  ownerChecked,
  onShare,
  onRestore,
  onSignOut,
  getToken,
  maintenance,
  maintenanceMessage,
  maintenanceReturnsAt,
  onToggleMaintenance,
  notice,
  onPublishNotice,
  mobileWarning,
  onSaveMobileWarning,
  viewerTips,
  onSaveViewerTips,
  welcomeMessage,
  onSaveWelcomeMessage,
  updatesData,
  onSaveUpdatesNotes,
  onSaveViewerUpdates,
  onOpenTrophies,
  trophyPulseTick,
  trophySettings,
  onSaveTrophySettings,
  onUploadTrophySound,
  onClearTrophySound,
  // Pledge-drive (Buy Me A Coffee) config — same server shape used
  // by the CRT ident. When `enabled: true` AND a URL is set, the
  // CRT interstitial fires on roughly 1 in `frequency` channel changes.
  pledgeDrive,
  onSavePledgeDrive,
  // Channel list — passed straight through to the TRIBUTE panel so
  // the operator can pin a tribute to a channel/tape from the same
  // dropdown. Optional (any missing prop degrades to "no linked
  // channel" gracefully).
  channels,
  // Videos-by-channel map — used only by the FEATURED manager for
  // its "pick a tape" dropdown. Safe to omit; the dropdown will just
  // stay empty until a channel is picked.
  videosByChannel,
  apiBase,
  authHeaders,
}) {
  const [showRestore, setShowRestore] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [showMaint, setShowMaint] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [showWelcomeMsg, setShowWelcomeMsg] = useState(false);
  const [welcomeSubtitleDraft, setWelcomeSubtitleDraft] = useState("");
  const [showTrophyAdmin, setShowTrophyAdmin] = useState(false);
  const [showTheme, setShowTheme] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [showAudio, setShowAudio] = useState(false);
  // Tribute / In Memoriam editor. Sits alongside INBOX / AUDIO / TIPS
  // in the SITE MESSAGES dropdown; a single evolving doc that any
  // viewer can open via the top-of-viewport banner published on save.
  const [showTribute, setShowTribute] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  // Pledge drive (Buy Me A Coffee) panel state.
  const [showPledge, setShowPledge] = useState(false);
  // Featured Show manager — owner picks entries that replace the
  // "Coming Up Next" panel in the Channel Guide.
  const [showFeatured, setShowFeatured] = useState(false);
  // ★ STATS analytics modal — live viewers, top channels, top tapes,
  // peak hours, referrers, signed-in vs anonymous split.
  const [showStats, setShowStats] = useState(false);
  const [pledgeDraft, setPledgeDraft] = useState({
    enabled: false,
    url: "",
    headline: "",
    subline: "",
    cta_label: "",
    frequency: 10,
  });
  const [pledgeSaving, setPledgeSaving] = useState(false);
  const { unread: inboxUnread, refresh: refreshInbox } = useUnreadInbox(isOwner, getToken);
  // Theme draft for the picker — initialised from the server fetch in the
  // effect below. Saving the theme broadcasts `crt:theme-updated` so other
  // parts of the app can re-render without a network round-trip.
  const [themeDraft, setThemeDraft] = useState({
    phosphor: "green",
    skin: "wood",
    room: "memphis",
    brand_text: "SCARTLEAD.TV",
    callsign: "CRT-1986",
    active_season: "none",
    live_overlay_enabled: false,
    live_overlay_text: "SCARTLEAD.TV",
    live_overlay_style: "boxed",
    live_overlay_position: "br",
    live_overlay_opacity: 75,
  });
  const [themeSaving, setThemeSaving] = useState(false);
  const [updatesNotesDraft, setUpdatesNotesDraft] = useState("");
  const [updatesFeaturesDraft, setUpdatesFeaturesDraft] = useState([]);
  // Viewer-tips drafts — initialised when the tips panel opens.
  const [tipsTitleDraft, setTipsTitleDraft] = useState("");
  const [tipsSoundDraft, setTipsSoundDraft] = useState("");
  const [tipsOffDraft, setTipsOffDraft] = useState("");
  // Trophy admin local drafts.
  const [trophyDisabledDraft, setTrophyDisabledDraft] = useState([]);
  const [soundUploading, setSoundUploading] = useState(false);
  // Drives the 3s phosphor pulse ring on the topbar trophy icon whenever a
  // new trophy is unlocked. We translate the parent's monotonically-bumped
  // tick into a transient "active" boolean.
  const [trophyIconPulse, setTrophyIconPulse] = React.useState(false);
  React.useEffect(() => {
    if (!trophyPulseTick) return undefined;
    setTrophyIconPulse(true);
    const t = setTimeout(() => setTrophyIconPulse(false), 3200);
    return () => clearTimeout(t);
  }, [trophyPulseTick]);
  // Master "SITE MESSAGES" dropdown that hosts the three sub-panels below.
  // Replaces the wrench / megaphone / smartphone trio that used to live
  // directly in the topbar.
  const [showMenu, setShowMenu] = useState(false);

  // ─── Nightly YouTube scan status ──────────────────────────────────────
  // Polls /api/site/youtube-scan-status while the owner is online so the
  // "⚠ N BLOCKED" pill stays fresh. Cheap (one tiny GET per minute) and
  // entirely owner-scoped — never fires for plain viewers.
  const [ytScan, setYtScan] = useState(null);
  const [showYtScan, setShowYtScan] = useState(false);
  useEffect(() => {
    if (!isOwner) { setYtScan(null); return undefined; }
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const token = typeof getToken === "function" ? getToken() : "";
        const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/site/youtube-scan-status`;
        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok || cancelled) return;
        const body = await res.json();
        if (!cancelled) setYtScan(body || null);
      } catch (e) { /* noop */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isOwner, getToken]);

  const dismissYtScan = async () => {
    try {
      const token = typeof getToken === "function" ? getToken() : "";
      await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/site/youtube-scan-status/acknowledge`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setYtScan((s) => (s ? { ...s, has_news: false, acknowledged_at: new Date().toISOString() } : s));
      setShowYtScan(false);
    } catch (e) { /* noop */ }
  };

  // Allow Shift+M (and any other dispatcher) to open the menu from outside
  // the component tree.
  React.useEffect(() => {
    const onOpen = () => { if (isOwner) setShowMenu(true); };
    window.addEventListener("crt:open-messages-menu", onOpen);
    return () => window.removeEventListener("crt:open-messages-menu", onOpen);
  }, [isOwner]);

  // ★ STATS "NEW SINCE LAST VISIT" badge — polls /api/stats/since with
  // the timestamp of the owner's last STATS panel open (persisted in
  // localStorage). Only meaningful when isOwner. Refreshed on mount +
  // every 60s while owner is signed in; also reset to `now` whenever
  // the STATS panel opens (see openSubPanel below).
  const [newVisitorsSinceCheck, setNewVisitorsSinceCheck] = useState(0);
  React.useEffect(() => {
    if (!isOwner) { setNewVisitorsSinceCheck(0); return undefined; }
    let cancelled = false;
    const poll = async () => {
      try {
        // Default lookback = 24h ago on first ever load, so a brand-new
        // owner gets an at-a-glance sense of traffic without needing
        // to have visited STATS before.
        let since = null;
        try { since = localStorage.getItem("crt_stats_last_check"); } catch (e) { /* noop */ }
        if (!since) {
          since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        }
        const res = await fetch(
          `${apiBase}/stats/since?since=${encodeURIComponent(since)}`,
          { credentials: "include", headers: authHeaders ? authHeaders() : {} }
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setNewVisitorsSinceCheck(Number.isFinite(data.count) ? data.count : 0);
      } catch (e) { /* silent — badge just stays at previous value */ }
    };
    poll();
    const iv = setInterval(poll, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isOwner, apiBase, authHeaders]);


  // Helper so menu items can switch sub-panels cleanly — close menu, open
  // exactly one panel (and close the others to avoid stacking).
  const openSubPanel = (which) => {
    setShowMaint(which === "maint");
    setShowNotice(which === "notice");
    setShowMobile(which === "mobile");
    setShowUpdates(which === "updates");
    setShowTips(which === "tips");
    setShowWelcomeMsg(which === "welcomeMsg");
    setShowTrophyAdmin(which === "trophies");
    setShowTheme(which === "theme");
    setShowInbox(which === "inbox");
    setShowTribute(which === "tribute");
    setShowAudio(which === "audio");
    setShowCategories(which === "categories");
    setShowPledge(which === "pledge");
    setShowFeatured(which === "featured");
    setShowStats(which === "stats");
    // When the owner opens STATS, mark "last check" = now so the
    // NEW SINCE LAST VISIT badge resets. Any events arriving after
    // this point will start ticking the counter back up on the next
    // 60s poll.
    if (which === "stats") {
      try { localStorage.setItem("crt_stats_last_check", new Date().toISOString()); } catch (e) { /* noop */ }
      setNewVisitorsSinceCheck(0);
    }
    setShowMenu(false);
  };
  // Any-message-active flag — drives the cog badge dot.
  const anyMessageActive = !!maintenance || !!(notice && notice.enabled);
  const [msgDraft, setMsgDraft] = useState("");
  const [returnEnabled, setReturnEnabled] = useState(false);
  const [returnInDraft, setReturnInDraft] = useState("15"); // minutes-from-now
  const [submitting, setSubmitting] = useState(false);
  // Notice board drafts — initialised when the panel opens (see effect below).
  const [noticeTitle, setNoticeTitle] = useState("");
  const [noticeBody, setNoticeBody] = useState("");
  const [noticeCta, setNoticeCta] = useState("CONTINUE");
  const [noticeUntilEnabled, setNoticeUntilEnabled] = useState(false);
  const [noticeUntilDraft, setNoticeUntilDraft] = useState(""); // datetime-local string
  // Scheduled-publish (delay-publishing) state — empty/disabled means
  // "publish now"; otherwise the notice stays dormant until publish_at.
  const [noticePublishAtEnabled, setNoticePublishAtEnabled] = useState(false);
  const [noticePublishAtDraft, setNoticePublishAtDraft] = useState("");
  // Mobile-warning panel drafts.
  const [mobileTitleDraft, setMobileTitleDraft] = useState("");
  const [mobileBodyDraft, setMobileBodyDraft] = useState("");

  // Sync the textarea draft with the persisted message + return time whenever
  // the panel opens or the upstream value changes.
  React.useEffect(() => {
    if (!showMaint) return;
    setMsgDraft(maintenanceMessage || "");
    if (maintenanceReturnsAt) {
      const ms = new Date(maintenanceReturnsAt).getTime() - Date.now();
      if (ms > 0) {
        setReturnEnabled(true);
        setReturnInDraft(String(Math.max(1, Math.round(ms / 60000))));
        return;
      }
    }
    setReturnEnabled(false);
    setReturnInDraft("15");
  }, [showMaint, maintenanceMessage, maintenanceReturnsAt]);

  // Same shape sync for the notice panel — populate drafts from server state
  // when opening so the owner can iterate on the existing announcement.
  React.useEffect(() => {
    if (!showNotice) return;
    setNoticeTitle((notice && notice.title) || "");
    setNoticeBody((notice && notice.body) || "");
    setNoticeCta((notice && notice.cta_label) || "CONTINUE");
    if (notice && notice.valid_until) {
      const d = new Date(notice.valid_until);
      if (Number.isFinite(d.getTime())) {
        // datetime-local needs `YYYY-MM-DDTHH:MM` in *local* time (no Z).
        const pad = (n) => String(n).padStart(2, "0");
        const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        setNoticeUntilEnabled(true);
        setNoticeUntilDraft(local);
        return;
      }
    }
    setNoticeUntilEnabled(false);
    setNoticeUntilDraft("");
    if (notice && notice.publish_at) {
      const d = new Date(notice.publish_at);
      if (Number.isFinite(d.getTime())) {
        const pad = (n) => String(n).padStart(2, "0");
        const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        setNoticePublishAtEnabled(true);
        setNoticePublishAtDraft(local);
        return;
      }
    }
    setNoticePublishAtEnabled(false);
    setNoticePublishAtDraft("");
  }, [showNotice, notice]);

  // Sync mobile-warning drafts from server state when panel opens.
  React.useEffect(() => {
    if (!showMobile) return;
    setMobileTitleDraft((mobileWarning && mobileWarning.title) || "");
    setMobileBodyDraft((mobileWarning && mobileWarning.body) || "");
  }, [showMobile, mobileWarning]);

  // Sync viewer-news cards + Station Bulletin when WHAT'S NEW opens.
  useEffect(() => {
    if (!showUpdates) return;
    setUpdatesNotesDraft((updatesData && updatesData.notes) || "");
    setUpdatesFeaturesDraft(Array.isArray(updatesData?.features) ? updatesData.features.map((item) => ({
      id: item.id || "", kicker: item.kicker || "NEW FEATURE", title: item.title || "", body: item.body || "", tag: item.tag || "NEW",
    })) : []);
  }, [showUpdates, updatesData]);
  const updateViewerFeatureDraft = (index, field, value) => setUpdatesFeaturesDraft((rows) => rows.map((row, i) => i === index ? { ...row, [field]: value } : row));
  const addViewerFeatureDraft = () => setUpdatesFeaturesDraft((rows) => [...rows.slice(0, 7), { id: `viewer-${Date.now()}`, kicker: "NEW FEATURE", title: "", body: "", tag: "NEW" }]);
  const removeViewerFeatureDraft = (index) => setUpdatesFeaturesDraft((rows) => rows.filter((_, i) => i !== index));

  // Sync viewer-tips drafts from server state when the tips panel opens.
  React.useEffect(() => {
    if (!showTips) return;
    setTipsTitleDraft((viewerTips && viewerTips.title) || "");
    setTipsSoundDraft((viewerTips && viewerTips.sound_line) || "");
    setTipsOffDraft((viewerTips && viewerTips.off_line) || "");
  }, [showTips, viewerTips]);

  // Sync welcome-message draft from server state when its panel opens.
  React.useEffect(() => {
    if (!showWelcomeMsg) return;
    setWelcomeSubtitleDraft((welcomeMessage && welcomeMessage.subtitle) || "");
  }, [showWelcomeMsg, welcomeMessage]);

  // Sync pledge-drive draft from server state whenever the panel opens.
  // The empty-string defaults for text fields mirror the backend's
  // "null = use default" convention — the field placeholders show the
  // built-in copy so the owner knows what'll render if they leave it
  // blank.
  React.useEffect(() => {
    if (!showPledge) return;
    setPledgeDraft({
      enabled: !!(pledgeDrive && pledgeDrive.enabled),
      url: (pledgeDrive && pledgeDrive.url) || "",
      headline: (pledgeDrive && pledgeDrive.headline) || "",
      subline: (pledgeDrive && pledgeDrive.subline) || "",
      cta_label: (pledgeDrive && pledgeDrive.cta_label) || "",
      frequency: (pledgeDrive && Number(pledgeDrive.frequency)) || 10,
    });
  }, [showPledge, pledgeDrive]);

  // Seed the trophy admin draft from the persisted disabled list whenever
  // the admin panel opens (so unsaved checkbox flips don't survive close).
  React.useEffect(() => {
    if (!showTrophyAdmin) return;
    setTrophyDisabledDraft(Array.isArray(trophySettings?.disabled) ? [...trophySettings.disabled] : []);
  }, [showTrophyAdmin, trophySettings]);

  // Seed theme picker draft when the panel opens. Reads from the server so
  // a stale local broadcast can't lock the picker out of the truth.
  React.useEffect(() => {
    if (!showTheme) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/site/theme`);
        if (!r.ok || cancelled) return;
        const t = await r.json();
        setThemeDraft({
          phosphor: t.phosphor || "green",
          skin: t.skin || "wood",
          room: t.room || "memphis",
          brand_text: t.brand_text || "SCARTLEAD.TV",
          callsign: t.callsign || "CRT-1986",
          active_season: t.active_season || "none",
          live_overlay_enabled: !!t.live_overlay_enabled,
          live_overlay_text: t.live_overlay_text || "SCARTLEAD.TV",
          live_overlay_style: t.live_overlay_style || "boxed",
          live_overlay_position: t.live_overlay_position || "br",
          live_overlay_opacity: typeof t.live_overlay_opacity === "number" ? t.live_overlay_opacity : 75,
        });
      } catch (e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [showTheme]);

  if (!ownerChecked) return null;

  return (
    <div className="absolute top-4 right-4 z-50 flex items-center gap-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>
      {/* Mode badge */}
      <div
        className={`px-3 py-1.5 text-[10px] tracking-[0.3em] border ${
          isOwner
            ? "border-[var(--phosphor)] text-[var(--phosphor)] shadow-[0_0_10px_color-mix(in srgb, var(--phosphor) 30%, transparent)]"
            : "border-amber-500/70 text-amber-400"
        }`}
        data-testid="owner-mode-badge"
      >
        {isOwner ? "● BROADCASTER" : "● VIEWER MODE"}
      </div>

      {/* Share */}
      <button
        onClick={onShare}
        data-testid="share-button"
        className="flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.3em] border border-[var(--phosphor)] text-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors"
      >
        <Share2 size={12} />
        SHARE
      </button>

      {/* Trophies — visible to all viewers, opens the trophy room modal. */}
      <button
        onClick={onOpenTrophies}
        data-testid="open-trophies"
        title="Trophy room"
        className={`flex items-center gap-1.5 px-2 py-1.5 text-[10px] tracking-[0.3em] border transition-colors ${
          trophyIconPulse
            ? "border-[var(--phosphor)] text-[var(--phosphor)] animate-trophy-pulse"
            : "border-neutral-700 text-neutral-300 hover:border-[var(--phosphor)] hover:text-[var(--phosphor)]"
        }`}
      >
        <Trophy size={12} />
      </button>

      {/* Owner controls */}
      {isOwner ? (
        <>
          {/* Nightly YT scan pill — only when fresh blocks await ack. */}
          {ytScan && ytScan.has_news && (
            <div className="relative">
              <button
                onClick={() => setShowYtScan((s) => !s)}
                data-testid="yt-scan-pill"
                title={`${ytScan.newly_blocked} YouTube tape${ytScan.newly_blocked === 1 ? "" : "s"} newly blocked`}
                className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] tracking-[0.3em] border border-[#FFCC00] text-[#FFCC00] bg-[#FFCC00]/10 hover:bg-[#FFCC00] hover:text-black transition-colors animate-pulse"
              >
                <AlertTriangle size={12} />
                {ytScan.newly_blocked} BLOCKED
              </button>
              {showYtScan && (
                <div
                  className="absolute top-full right-0 mt-2 w-72 p-3 border border-[#FFCC00]/70 bg-black/95 shadow-[0_0_30px_rgba(255,204,0,0.25)] text-[#FFCC00] text-[11px] leading-relaxed"
                  data-testid="yt-scan-popover"
                  style={{ fontFamily: "VT323, monospace" }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold tracking-widest">⚠ NIGHTLY YT SCAN</span>
                    <button
                      onClick={() => setShowYtScan(false)}
                      className="opacity-70 hover:opacity-100"
                      data-testid="yt-scan-close"
                      aria-label="close"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <div className="opacity-80 mb-2">
                    {ytScan.newly_blocked} newly blocked · {ytScan.blocked_total} total blocked · {ytScan.scanned} scanned
                  </div>
                  {Array.isArray(ytScan.newly_blocked_titles) && ytScan.newly_blocked_titles.length > 0 && (
                    <ul className="list-disc pl-4 space-y-0.5 mb-3 max-h-32 overflow-y-auto scrollbar-crt">
                      {ytScan.newly_blocked_titles.map((t, i) => (
                        <li key={`yt-title-${i}-${(t || "").slice(0, 24)}`} className="truncate" data-testid={`yt-scan-title-${i}`}>{t || "(untitled)"}</li>
                      ))}
                    </ul>
                  )}
                  <div className="text-[10px] opacity-70 mb-3">
                    Open MENU → CURRENT PLAYLIST → PURGE BLOCKED to remove them, or DISMISS to clear this notice.
                  </div>
                  <button
                    onClick={dismissYtScan}
                    data-testid="yt-scan-dismiss"
                    className="w-full border border-[#FFCC00] py-1 text-[10px] tracking-widest hover:bg-[#FFCC00] hover:text-black transition-colors"
                  >
                    DISMISS
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Phase 2.3.69 — STATS owns the former storage-status slot. */}
          <button type="button" onClick={() => openSubPanel("stats")} data-testid="owner-stats-pill"
            title={newVisitorsSinceCheck > 0 ? `${newVisitorsSinceCheck} new viewers since last STATS check` : "Open accurate viewer statistics"}
            className={`relative flex items-center gap-1.5 px-2 py-1.5 text-[10px] tracking-[0.3em] border transition-colors ${newVisitorsSinceCheck > 0 ? "border-[var(--phosphor)] text-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_10%,transparent)]" : "border-neutral-700 text-neutral-300 hover:border-[var(--phosphor)] hover:text-[var(--phosphor)]"}`}>
            <Activity size={12} /> STATS
            {newVisitorsSinceCheck > 0 && <span data-testid="owner-stats-pill-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FFCC00] text-black text-[9px] tracking-normal flex items-center justify-center font-bold">{newVisitorsSinceCheck}</span>}
          </button>

          {/* INBOX unread pill — only when there are unread transmissions.
              Mirrors the YT-scan pill shape so the topbar reads consistently. */}
          {inboxUnread > 0 && (
            <button
              onClick={() => openSubPanel("inbox")}
              data-testid="inbox-pill"
              title={`${inboxUnread} unread message${inboxUnread === 1 ? "" : "s"}`}
              className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] tracking-[0.3em] border border-[var(--phosphor)] text-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_10%,transparent)] hover:bg-[var(--phosphor)] hover:text-black transition-colors animate-pulse"
            >
              <Inbox size={12} />
              {inboxUnread} NEW
            </button>
          )}

          <button
            onClick={() => setShowMenu((s) => !s)}
            data-testid="toggle-messages-menu"
            title="Site messages — maintenance, broadcast notice, mobile warning"
            className={`relative p-2 border transition-colors flex items-center gap-1.5 ${
              anyMessageActive
                ? "border-[var(--phosphor)] text-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_10%,transparent)] shadow-[0_0_10px_color-mix(in srgb, var(--phosphor) 35%, transparent)] hover:bg-[var(--phosphor)] hover:text-black"
                : "border-neutral-700 text-neutral-300 hover:border-[var(--phosphor)] hover:text-[var(--phosphor)]"
            }`}
          >
            <MessageSquare size={14} />
            <span className="text-[9px] tracking-[0.2em] hidden md:inline">MESSAGES</span>
            {anyMessageActive && (
              <span
                className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#FFCC00] shadow-[0_0_6px_rgba(255,204,0,0.9)]"
                data-testid="messages-badge-dot"
                title="Active site message"
              />
            )}
          </button>
          <button
            onClick={() => setShowKey((s) => !s)}
            data-testid="show-owner-key"
            title="Show owner key"
            className="p-2 border border-neutral-700 text-neutral-300 hover:border-[var(--phosphor)] hover:text-[var(--phosphor)] transition-colors"
          >
            <KeyRound size={14} />
          </button>
          <button
            onClick={onSignOut}
            data-testid="signout-owner"
            title="Sign out (viewer mode)"
            className="p-2 border border-neutral-700 text-neutral-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
          >
            <LogOut size={14} />
          </button>
        </>
      ) : (
        <button
          onClick={() => setShowRestore(true)}
          data-testid="restore-owner-button"
          title="Restore owner access"
          className="p-2 border border-neutral-700 text-neutral-300 hover:border-[var(--phosphor)] hover:text-[var(--phosphor)] transition-colors"
        >
          <KeyRound size={14} />
        </button>
      )}

      {/* Site messages master dropdown — single entry-point for the three
          editorial sub-panels (maintenance / notice / mobile warning). Acts
          like a folder; clicking a row pops the appropriate sub-panel and
          closes this dropdown to avoid stacking. */}
      {showMenu && isOwner && (
        <div
          className="absolute top-12 right-0 w-[300px] bg-black border-2 border-[var(--phosphor)] p-2 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 35%, transparent)]"
          data-testid="messages-menu"
        >
          <div className="flex items-center justify-between px-2 py-1 mb-1">
            <div className="text-xs tracking-[0.3em]">┌─ SITE MESSAGES ─┐</div>
            <button onClick={() => setShowMenu(false)} className="text-[var(--phosphor)]">
              <X size={14} />
            </button>
          </div>
          {/* INBOX row — submissions from the ★ CONTACT virtual channel.
              Sits at the top so unread mail is the first thing the owner
              sees when opening the menu. */}
          <button
            onClick={() => openSubPanel("inbox")}
            data-testid="menu-row-inbox"
            className="w-full flex items-start gap-3 p-2 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Inbox size={16} className={inboxUnread > 0 ? "text-[var(--phosphor)] mt-0.5 shrink-0" : "text-neutral-400 mt-0.5 shrink-0"} />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em] flex items-center gap-2">
                INBOX
                {inboxUnread > 0 && (
                  <span
                    className="text-[9px] px-1.5 py-0.5 bg-[#FFCC00] text-black tracking-widest font-bold"
                    data-testid="menu-inbox-unread"
                  >
                    {inboxUnread} NEW
                  </span>
                )}
              </div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Read messages submitted via the ★ CONTACT channel.
              </div>
            </div>
          </button>
          {/* AUDIO row — station-wide loudness target + per-video
              backfill. Sits right after INBOX so audio/inbox admin live
              next to each other in the menu. */}
          <button
            onClick={() => openSubPanel("audio")}
            data-testid="menu-row-audio"
            className="w-full flex items-start gap-3 p-2 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Volume2 size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">AUDIO</div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Set the station&apos;s loudness target + backfill old tapes.
              </div>
            </div>
          </button>
          {/* TRIBUTE row — In Memoriam editor. One evolving tribute
              (name, dates, obituary, hero image URL, audio URL, gallery
              of photo URLs, linked channel/tape). Publishing pushes a
              24h "▲ IN MEMORIAM" banner to every viewer. */}
          <button
            onClick={() => openSubPanel("tribute")}
            data-testid="menu-row-tribute"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Heart size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">TRIBUTE</div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Announce the passing of an actor / actress. Photos, audio, obituary + a link to their work.
              </div>
            </div>
          </button>
          {/* MAINTENANCE row */}
          <button
            onClick={() => openSubPanel("maint")}
            data-testid="menu-row-maintenance"
            className="w-full flex items-start gap-3 p-2 border border-transparent hover:border-[#FFCC00] hover:bg-[#FFCC00]/5 transition-colors text-left"
          >
            <Wrench size={16} className={maintenance ? "text-[#FFCC00] mt-0.5 shrink-0" : "text-neutral-400 mt-0.5 shrink-0"} />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em] flex items-center gap-2">
                MAINTENANCE
                {maintenance && (
                  <span className="text-[9px] px-1.5 py-0.5 bg-[#FFCC00] text-black tracking-widest font-bold">LIVE</span>
                )}
              </div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Take the site offline with a custom stand-by message.
              </div>
            </div>
          </button>
          {/* NOTICE BOARD row */}
          <button
            onClick={() => openSubPanel("notice")}
            data-testid="menu-row-notice"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Megaphone size={16} className={notice && notice.enabled ? "text-[var(--phosphor)] mt-0.5 shrink-0" : "text-neutral-400 mt-0.5 shrink-0"} />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em] flex items-center gap-2">
                NOTICE BOARD
                {notice && notice.enabled && (
                  <span className="text-[9px] px-1.5 py-0.5 bg-[var(--phosphor)] text-black tracking-widest font-bold">LIVE</span>
                )}
              </div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Click-to-continue announcement shown to every viewer.
              </div>
            </div>
          </button>
          {/* MOBILE WARNING row */}
          <button
            onClick={() => openSubPanel("mobile")}
            data-testid="menu-row-mobile"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Smartphone size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">MOBILE WARNING</div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Copy shown to viewers on screens under 1024px.
              </div>
            </div>
          </button>
          {/* WHAT'S NEW row — release notes for the UPDATES button. */}
          <button
            onClick={() => openSubPanel("updates")}
            data-testid="menu-row-updates"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Sparkles size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">WHAT&apos;S NEW</div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Release notes shown when viewers tap UPDATES on the remote.
              </div>
            </div>
          </button>
          {/* VIEWER TIPS row — copy for the combined sound + fullscreen banner. */}
          <button
            onClick={() => openSubPanel("tips")}
            data-testid="menu-row-tips"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <MessageSquare size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">VIEWER TIPS</div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Edit the in-screen &ldquo;click for sound&rdquo; / &ldquo;dbl-click for fullscreen&rdquo; prompt.
              </div>
            </div>
          </button>
          {/* WELCOME MESSAGE row — subtitle line shown to signed-in viewers. */}
          <button
            onClick={() => openSubPanel("welcomeMsg")}
            data-testid="menu-row-welcome-msg"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Sparkles size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">WELCOME MESSAGE</div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Edit the subtitle shown inside the WELCOME BACK bumper after sign-in.
              </div>
            </div>
          </button>
          {/* TROPHY ADMIN row — toggle, override copy, upload confirmation sound. */}
          <button
            onClick={() => openSubPanel("trophies")}
            data-testid="menu-row-trophies"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Trophy size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">TROPHIES</div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Toggle individual trophies and upload an unlock confirmation sound.
              </div>
            </div>
          </button>
          {/* CATEGORIES row — Sky Q-style rails the guide groups channels by. */}
          <button
            onClick={() => openSubPanel("categories")}
            data-testid="menu-row-categories"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Layers size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">CATEGORIES</div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Add/rename/reorder the rails (Movies, Music, Kids…) the guide groups channels by.
              </div>
            </div>
          </button>
          {/* PLEDGE DRIVE row — Buy Me A Coffee interstitial config. */}
          <button
            onClick={() => openSubPanel("pledge")}
            data-testid="menu-row-pledge"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Coffee size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">
                PLEDGE DRIVE
                {pledgeDrive && pledgeDrive.enabled && pledgeDrive.url && (
                  <span className="ml-2 text-[9px] text-[var(--phosphor)] opacity-80">● LIVE</span>
                )}
              </div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                A retro &ldquo;SUPPORT THE STATION&rdquo; ident that fires on channel change and links to your BMC.
              </div>
            </div>
          </button>
          {/* THEME row — cabinet skin, phosphor colour and brand. */}
          <button
            onClick={() => openSubPanel("theme")}
            data-testid="menu-row-theme"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Palette size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">★ THEME</div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Cabinet skin · phosphor colour · brand text.
              </div>
            </div>
          </button>
          {/* FEATURED row — curate hero entries that replace "Coming Up
              Next" in the Channel Guide. Great for promoting new movies,
              upcoming shows, or events. */}
          <button
            onClick={() => openSubPanel("featured")}
            data-testid="menu-row-featured"
            className="w-full flex items-start gap-3 p-2 mt-1 border border-transparent hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors text-left"
          >
            <Star size={16} className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] tracking-[0.2em]">★ FEATURED</div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">
                Rotating hero spotlight in the Channel Guide — promote a movie / show / event.
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Maintenance control panel — edit stand-by message + toggle on/off */}
      {showMaint && isOwner && (
        <div
          className="absolute top-12 right-0 w-[380px] bg-black border-2 border-[#FFCC00] p-4 text-[#FFCC00] shadow-[0_0_20px_rgba(255,204,0,0.35)]"
          data-testid="maintenance-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs tracking-[0.3em]">
              ┌─ EPG UPDATE / STAND-BY ─┐
            </div>
            <button onClick={() => setShowMaint(false)} className="text-[#FFCC00]">
              <X size={14} />
            </button>
          </div>
          <div className="text-[10px] opacity-80 mb-2">
            Current status:{" "}
            <span className={maintenance ? "text-[#FFCC00] font-bold" : "text-[var(--phosphor)] font-bold"}>
              {maintenance ? "OFFLINE TO VIEWERS" : "ON AIR"}
            </span>
          </div>
          <label className="block text-[10px] opacity-80 mb-1 tracking-widest">
            STAND-BY MESSAGE
          </label>
          <textarea
            value={msgDraft}
            onChange={(e) => setMsgDraft(e.target.value.slice(0, 300))}
            rows={3}
            maxLength={300}
            placeholder="Currently updating the EPG, we'll be right back..."
            data-testid="maintenance-message-input"
            className="w-full bg-black border border-[#FFCC00] text-[#FFCC00] p-2 outline-none text-[11px] resize-none placeholder:text-[#FFCC00]/40"
          />
          <div className="text-[9px] opacity-60 mb-3 text-right tabular-nums">
            {msgDraft.length}/300
          </div>

          {/* Optional scheduled-return timer */}
          <div className="mb-3 border-t border-[#FFCC00]/30 pt-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={returnEnabled}
                onChange={(e) => setReturnEnabled(e.target.checked)}
                data-testid="return-timer-toggle"
                className="accent-[#FFCC00]"
              />
              <span className="text-[10px] tracking-[0.2em]">
                SHOW &quot;BACK IN&quot; COUNTDOWN ON STAND-BY PAGE
              </span>
            </label>
            {returnEnabled && (
              <div className="mt-2 flex items-center gap-2 pl-6">
                <span className="text-[10px] opacity-80">Back in</span>
                <input
                  type="number"
                  min="1"
                  max="600"
                  value={returnInDraft}
                  onChange={(e) => setReturnInDraft(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
                  data-testid="return-timer-minutes"
                  className="w-16 bg-black border border-[#FFCC00] text-[#FFCC00] p-1 text-xs outline-none tabular-nums text-center"
                />
                <span className="text-[10px] opacity-80">minutes</span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={async () => {
                setSubmitting(true);
                try {
                  const returnsAt = returnEnabled
                    ? new Date(Date.now() + Math.max(1, parseInt(returnInDraft, 10) || 15) * 60000).toISOString()
                    : "";
                  await onToggleMaintenance({
                    enabled: !maintenance,
                    message: msgDraft,
                    returns_at: returnsAt,
                  });
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              data-testid="maintenance-action-button"
              className={`flex-1 py-2 text-xs tracking-[0.2em] font-bold transition-colors disabled:opacity-50 ${
                maintenance
                  ? "border-2 border-[var(--phosphor)] text-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black"
                  : "border-2 border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black"
              }`}
            >
              {submitting ? "..." : maintenance ? ">> BACK ON AIR <<" : ">> GO OFFLINE <<"}
            </button>
          </div>
          {maintenance && (
            <button
              onClick={async () => {
                setSubmitting(true);
                try {
                  const returnsAt = returnEnabled
                    ? new Date(Date.now() + Math.max(1, parseInt(returnInDraft, 10) || 15) * 60000).toISOString()
                    : "";
                  await onToggleMaintenance({
                    enabled: true,
                    message: msgDraft,
                    returns_at: returnsAt,
                  });
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting || (!msgDraft.trim() && !returnEnabled)}
              data-testid="maintenance-update-message"
              className="w-full mt-2 py-1.5 text-[10px] tracking-[0.2em] border border-[#FFCC00]/60 text-[#FFCC00]/80 hover:bg-[#FFCC00]/15 transition-colors disabled:opacity-40"
            >
              UPDATE MESSAGE / TIMER ONLY
            </button>
          )}
        </div>
      )}

      {/* Notice board panel — owner authors a click-to-continue announcement
          that every viewer must dismiss before they see videos. Saving the
          panel republishes (new notice_id), forcing previously-acked viewers
          to see it again on next load. */}
      {showNotice && isOwner && (
        <div
          className="absolute top-12 right-0 w-[400px] bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 35%, transparent)]"
          data-testid="notice-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs tracking-[0.3em]">┌─ NOTICE BOARD ─┐</div>
            <button onClick={() => setShowNotice(false)} className="text-[var(--phosphor)]">
              <X size={14} />
            </button>
          </div>
          <div className="text-[10px] opacity-80 mb-2">
            Status:{" "}
            <span className={notice && notice.enabled ? "text-[var(--phosphor)] font-bold" : "text-neutral-400 font-bold"}>
              {notice && notice.enabled ? "LIVE — viewers will be prompted" : "NOT SHOWING"}
            </span>
          </div>

          <label className="block text-[10px] opacity-80 mb-1 tracking-widest">TITLE (OPTIONAL)</label>
          <input
            type="text"
            value={noticeTitle}
            onChange={(e) => setNoticeTitle(e.target.value.slice(0, 120))}
            maxLength={120}
            placeholder="e.g. SITE UPDATE"
            data-testid="notice-title-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] mb-2"
          />

          <label className="block text-[10px] opacity-80 mb-1 tracking-widest">MESSAGE</label>
          <textarea
            value={noticeBody}
            onChange={(e) => setNoticeBody(e.target.value.slice(0, 1500))}
            rows={5}
            maxLength={1500}
            placeholder="Tell viewers what's new, what's broken, or what's coming..."
            data-testid="notice-body-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] resize-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
          />
          <div className="text-[9px] opacity-60 mb-3 text-right tabular-nums">
            {noticeBody.length}/1500
          </div>

          <label className="block text-[10px] opacity-80 mb-1 tracking-widest">BUTTON LABEL</label>
          <input
            type="text"
            value={noticeCta}
            onChange={(e) => setNoticeCta(e.target.value.slice(0, 24))}
            maxLength={24}
            placeholder="CONTINUE"
            data-testid="notice-cta-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] uppercase tracking-widest placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] mb-3"
          />

          <div className="mb-3 border-t border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] pt-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={noticeUntilEnabled}
                onChange={(e) => setNoticeUntilEnabled(e.target.checked)}
                data-testid="notice-until-toggle"
                className="accent-[var(--phosphor)]"
              />
              <span className="text-[10px] tracking-[0.2em]">AUTO-HIDE AFTER A DATE</span>
            </label>
            {noticeUntilEnabled && (
              <input
                type="datetime-local"
                value={noticeUntilDraft}
                onChange={(e) => setNoticeUntilDraft(e.target.value)}
                data-testid="notice-until-input"
                className="mt-2 ml-6 bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1 text-xs outline-none tabular-nums"
              />
            )}
          </div>

          <div className="mb-3 border-t border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] pt-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={noticePublishAtEnabled}
                onChange={(e) => setNoticePublishAtEnabled(e.target.checked)}
                data-testid="notice-schedule-toggle"
                className="accent-[var(--phosphor)]"
              />
              <span className="text-[10px] tracking-[0.2em]">SCHEDULE FOR LATER</span>
            </label>
            {noticePublishAtEnabled && (
              <>
                <input
                  type="datetime-local"
                  value={noticePublishAtDraft}
                  onChange={(e) => setNoticePublishAtDraft(e.target.value)}
                  data-testid="notice-schedule-input"
                  className="mt-2 ml-6 bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1 text-xs outline-none tabular-nums"
                />
                <div className="text-[9px] opacity-60 ml-6 mt-1 leading-snug">
                  Notice stays dormant until this time, then appears for viewers automatically.
                </div>
              </>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={async () => {
                if (!noticeBody.trim()) return;
                setSubmitting(true);
                try {
                  const validUntil = noticeUntilEnabled && noticeUntilDraft
                    ? new Date(noticeUntilDraft).toISOString()
                    : "";
                  const publishAt = noticePublishAtEnabled && noticePublishAtDraft
                    ? new Date(noticePublishAtDraft).toISOString()
                    : "";
                  const ok = await onPublishNotice({
                    enabled: true,
                    title: noticeTitle,
                    body: noticeBody,
                    cta_label: noticeCta,
                    valid_until: validUntil,
                    publish_at: publishAt,
                  });
                  if (ok) setShowNotice(false);
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting || !noticeBody.trim()}
              data-testid="notice-publish-button"
              className="flex-1 py-2 text-xs tracking-[0.2em] font-bold border-2 border-[var(--phosphor)] text-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
            >
              {submitting ? "..." : ">> PUBLISH <<"}
            </button>
            {notice && notice.enabled && (
              <button
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    await onPublishNotice({ enabled: false });
                    setShowNotice(false);
                  } finally {
                    setSubmitting(false);
                  }
                }}
                disabled={submitting}
                data-testid="notice-stop-button"
                className="py-2 px-3 text-[10px] tracking-[0.2em] border border-[#FF3B30]/70 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black transition-colors disabled:opacity-40"
              >
                STOP
              </button>
            )}
          </div>
          <div className="text-[9px] opacity-50 mt-2 leading-relaxed">
            Saving republishes — previously-acked viewers will see it again on next load.
          </div>
        </div>
      )}

      {/* Mobile-warning copy editor — what phone / small-tablet viewers see
          on first visit. No enable/disable here: the warning is tied to the
          viewport-size detection. Empty fields reset to defaults server-side. */}
      {showMobile && isOwner && (
        <div
          className="absolute top-12 right-0 w-[400px] bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 35%, transparent)]"
          data-testid="mobile-warning-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs tracking-[0.3em]">┌─ MOBILE WARNING ─┐</div>
            <button onClick={() => setShowMobile(false)} className="text-[var(--phosphor)]">
              <X size={14} />
            </button>
          </div>
          <div className="text-[10px] opacity-70 mb-2 leading-relaxed">
            Shown to viewers on screens narrower than 1024px (phones &amp; tablets in portrait).
          </div>

          <label className="block text-[10px] opacity-80 mb-1 tracking-widest">TITLE</label>
          <input
            type="text"
            value={mobileTitleDraft}
            onChange={(e) => setMobileTitleDraft(e.target.value.slice(0, 80))}
            maxLength={80}
            placeholder="BIG SCREEN ONLY"
            data-testid="mobile-title-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] uppercase tracking-widest placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] mb-2"
          />

          <label className="block text-[10px] opacity-80 mb-1 tracking-widest">MESSAGE</label>
          <textarea
            value={mobileBodyDraft}
            onChange={(e) => setMobileBodyDraft(e.target.value.slice(0, 800))}
            rows={6}
            maxLength={800}
            placeholder="Explain why mobile isn't ideal and what the viewer should do instead..."
            data-testid="mobile-body-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] resize-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
          />
          <div className="text-[9px] opacity-60 mb-3 text-right tabular-nums">
            {mobileBodyDraft.length}/800
          </div>

          <div className="flex gap-2">
            <button
              onClick={async () => {
                setSubmitting(true);
                try {
                  const ok = await onSaveMobileWarning({
                    title: mobileTitleDraft,
                    body: mobileBodyDraft,
                  });
                  if (ok) setShowMobile(false);
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              data-testid="mobile-save-button"
              className="flex-1 py-2 text-xs tracking-[0.2em] font-bold border-2 border-[var(--phosphor)] text-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
            >
              {submitting ? "..." : ">> SAVE <<"}
            </button>
            <button
              onClick={async () => {
                // Restore defaults by sending empty strings.
                setSubmitting(true);
                try {
                  const ok = await onSaveMobileWarning({ title: "", body: "" });
                  if (ok) {
                    setMobileTitleDraft("");
                    setMobileBodyDraft("");
                  }
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              data-testid="mobile-reset-button"
              className="py-2 px-3 text-[10px] tracking-[0.2em] border border-neutral-600 text-neutral-300 hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors disabled:opacity-40"
            >
              RESET
            </button>
          </div>
        </div>
      )}

      {/* WHAT'S NEW — curated viewer-facing feature cards plus optional Station Bulletin. */}
      {showUpdates && isOwner && (
        <div className="absolute top-12 right-0 w-[560px] max-h-[82vh] overflow-y-auto bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 35%, transparent)]" data-testid="updates-panel">
          <div className="flex items-center justify-between mb-2"><div className="text-xs tracking-[0.3em]">┌─ WHAT&apos;S NEW ─┐</div><button onClick={() => setShowUpdates(false)} className="text-[var(--phosphor)]"><X size={14} /></button></div>
          <div className="text-[10px] opacity-70 mb-4 leading-relaxed">Publish only things viewers can see, use or enjoy. Internal fixes, migrations and maintenance stay off the telly. Recently Added is still automatic.</div>
          <div className="flex items-center justify-between gap-3 mb-2"><label className="text-[10px] opacity-80 tracking-widest">VIEWER FEATURE CARDS</label><button type="button" onClick={addViewerFeatureDraft} disabled={updatesFeaturesDraft.length >= 8} className="px-2 py-1 text-[9px] border border-[var(--phosphor)] disabled:opacity-35" data-testid="updates-feature-add">+ ADD CARD</button></div>
          <div className="space-y-3 mb-3" data-testid="updates-feature-editor">{updatesFeaturesDraft.map((item, index) => (
            <div key={item.id || index} className="border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] p-3">
              <div className="flex items-center justify-between mb-2"><div className="text-[9px] opacity-55">CARD {index + 1}</div><button type="button" onClick={() => removeViewerFeatureDraft(index)} className="text-[9px] border border-red-500/55 text-red-300 px-2 py-1">REMOVE</button></div>
              <div className="grid grid-cols-2 gap-2 mb-2"><input value={item.kicker} onChange={(e) => updateViewerFeatureDraft(index, "kicker", e.target.value.slice(0, 32))} placeholder="TV GUIDE" className="bg-black border border-[var(--phosphor)] p-2 text-[11px]" /><input value={item.tag} onChange={(e) => updateViewerFeatureDraft(index, "tag", e.target.value.slice(0, 18))} placeholder="NEW" className="bg-black border border-[var(--phosphor)] p-2 text-[11px]" /></div>
              <input value={item.title} onChange={(e) => updateViewerFeatureDraft(index, "title", e.target.value.slice(0, 90))} placeholder="THE GUIDE JUST WENT PRIME TIME" className="w-full bg-black border border-[var(--phosphor)] p-2 text-[11px] mb-2" />
              <textarea value={item.body} onChange={(e) => updateViewerFeatureDraft(index, "body", e.target.value.slice(0, 420))} rows={3} placeholder="Grab the remote..." className="w-full bg-black border border-[var(--phosphor)] p-2 text-[11px] resize-none" />
            </div>
          ))}</div>
          <button onClick={async () => { setSubmitting(true); try { await onSaveViewerUpdates(updatesFeaturesDraft); } finally { setSubmitting(false); } }} disabled={submitting} data-testid="updates-features-publish" className="w-full py-2 mb-5 text-xs font-bold border-2 border-[var(--phosphor)]">{submitting ? "..." : ">> PUBLISH VIEWER UPDATES <<"}</button>
          <div className="border-t border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] pt-4"><label className="block text-[10px] opacity-80 mb-1 tracking-widest">STATION BULLETIN · OPTIONAL</label><textarea value={updatesNotesDraft} onChange={(e) => setUpdatesNotesDraft(e.target.value.slice(0, 3000))} rows={6} maxLength={3000} placeholder="Anything else viewers should know?" data-testid="updates-notes-input" className="w-full bg-black border border-[var(--phosphor)] p-2 text-[12px] resize-none" /><div className="text-[9px] opacity-60 mb-3 text-right">{updatesNotesDraft.length}/3000</div><button onClick={async () => { setSubmitting(true); try { const ok = await onSaveUpdatesNotes(updatesNotesDraft); if (ok) setShowUpdates(false); } finally { setSubmitting(false); } }} disabled={submitting} data-testid="updates-save-button" className="w-full py-2 text-xs border border-[var(--phosphor)]">{submitting ? "..." : "SAVE STATION BULLETIN"}</button></div>
        </div>
      )}

      {/* VIEWER TIPS — editor for the combined sound + fullscreen hint card
          rendered inside the CRT screen. All three fields support empty
          strings to reset to defaults on the backend. */}
      {showTips && isOwner && (
        <div
          className="absolute top-12 right-0 w-[400px] bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 35%, transparent)]"
          data-testid="tips-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs tracking-[0.3em]">┌─ VIEWER TIPS ─┐</div>
            <button onClick={() => setShowTips(false)} className="text-[var(--phosphor)]">
              <X size={14} />
            </button>
          </div>
          <div className="text-[10px] opacity-70 mb-3 leading-relaxed">
            The hint card shown in the centre of the screen until viewers click
            for sound or enter fullscreen. Lines hide themselves once that
            action is taken. Empty fields reset to default copy.
          </div>

          <label className="block text-[10px] opacity-80 mb-1 tracking-widest">TITLE</label>
          <input
            value={tipsTitleDraft}
            onChange={(e) => setTipsTitleDraft(e.target.value.slice(0, 60))}
            maxLength={60}
            placeholder="VIEWER TIPS"
            data-testid="tips-title-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] mb-3 placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono"
          />

          <label className="block text-[10px] opacity-80 mb-1 tracking-widest">SOUND LINE</label>
          <input
            value={tipsSoundDraft}
            onChange={(e) => setTipsSoundDraft(e.target.value.slice(0, 120))}
            maxLength={120}
            placeholder="CLICK ANYWHERE TO ENABLE SOUND"
            data-testid="tips-sound-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] mb-3 placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono"
          />

          <label className="block text-[10px] opacity-80 mb-1 tracking-widest">OFF LINE</label>
          <div className="text-[10px] opacity-60 mb-1 leading-snug">
            Shown in the centre of the screen while the TV is powered off
            (the default state for new viewers). Doubles as a &quot;tap to turn
            on&quot; affordance — the screen itself is clickable.
          </div>
          <input
            value={tipsOffDraft}
            onChange={(e) => setTipsOffDraft(e.target.value.slice(0, 120))}
            maxLength={120}
            placeholder="PRESS POWER OR CLICK SCREEN TO TUNE IN"
            data-testid="tips-off-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] mb-3 placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono"
          />

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={async () => {
                setSubmitting(true);
                try {
                  const ok = await onSaveViewerTips({
                    title: tipsTitleDraft,
                    sound_line: tipsSoundDraft,
                    off_line: tipsOffDraft,
                  });
                  if (ok) setShowTips(false);
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              data-testid="tips-save-button"
              className="py-2 text-xs tracking-[0.2em] font-bold border-2 border-[var(--phosphor)] text-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
            >
              {submitting ? "..." : ">> SAVE <<"}
            </button>
            <button
              onClick={async () => {
                setSubmitting(true);
                try {
                  const ok = await onSaveViewerTips({ title: "", sound_line: "", off_line: "" });
                  if (ok) {
                    setTipsTitleDraft("");
                    setTipsSoundDraft("");
                    setTipsOffDraft("");
                  }
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              data-testid="tips-reset-button"
              className="py-2 text-xs tracking-[0.2em] border border-neutral-600 text-neutral-300 hover:border-[var(--phosphor)] hover:text-[var(--phosphor)] transition-colors disabled:opacity-40"
            >
              RESET
            </button>
          </div>
        </div>
      )}

      {/* WELCOME MESSAGE — subtitle that appears in the WELCOME BACK bumper
          right after a viewer signs in. Empty draft resets to default
          server-side ("SIGNAL RESTORED"). Supports rich-text tokens like
          {name}, {newtapes}, {ch}, {date} — interpolated at render time. */}
      {showWelcomeMsg && isOwner && (
        <div
          className="absolute top-12 right-0 w-[440px] max-h-[80vh] overflow-y-auto bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 35%, transparent)]"
          data-testid="welcome-msg-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs tracking-[0.3em]">┌─ WELCOME MESSAGE ─┐</div>
            <button onClick={() => setShowWelcomeMsg(false)} className="text-[var(--phosphor)]">
              <X size={14} />
            </button>
          </div>
          <div className="text-[10px] opacity-70 mb-3 leading-snug">
            Body shown inside the welcome banner at the top of the channel
            guide after a viewer signs in. Up to 200 characters. Leave blank
            to keep the default fallback (viewer&apos;s name + new-tape
            count).
            <span className="text-[var(--phosphor)] phosphor-glow"> When you set a custom message, the default fallback is replaced —</span>
            use {"{name}"} and {"{newtapes}"} tokens below if you still want
            those bits inside your copy.
          </div>

          <label className="block text-[10px] tracking-[0.2em] opacity-70 mb-1">SUBTITLE</label>
          <textarea
            value={welcomeSubtitleDraft}
            onChange={(e) => setWelcomeSubtitleDraft(e.target.value.slice(0, 200))}
            maxLength={200}
            rows={3}
            placeholder="Welcome back, {name} — {newtapes} since you last tuned in."
            data-testid="welcome-msg-subtitle-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] mb-1 placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono"
          />
          <div className="text-[9px] opacity-50 tracking-[0.15em] mb-3 text-right">
            {welcomeSubtitleDraft.length} / 200
          </div>

          {/* Live preview — mirrors the actual banner that renders at the
              top of the channel guide (callsign chip + hero chromatic
              copy). Sample values let the owner see how their dynamic
              tokens will resolve before saving. */}
          <div className="mb-3">
            <div className="text-[9px] opacity-50 tracking-[0.2em] mb-1">PREVIEW · IN-GUIDE BANNER</div>
            <div
              className="border border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_8%,transparent)] px-4 py-3 phosphor-glow"
              style={{ fontFamily: "VT323, monospace" }}
              data-testid="welcome-msg-preview-wrap"
            >
              <div className="text-[10px] tracking-[0.5em] opacity-70 mb-2">
                ▣ CRT-1986 ▣
              </div>
              {welcomeSubtitleDraft.trim() ? (
                <div
                  className="text-xl md:text-2xl tracking-[0.05em] leading-snug whitespace-pre-line chromatic font-bold"
                  data-testid="welcome-msg-preview"
                >
                  {interpolateWelcome(welcomeSubtitleDraft, {
                    viewerName: "Lex Carter",
                    newCount: 3,
                    channelName: "NIGHTCRAWLER MOVIE",
                    channelNumberLabel: "03",
                    callsign: "CRT-1986",
                  })}
                </div>
              ) : (
                <>
                  <div className="text-xl md:text-2xl tracking-[0.1em] chromatic font-bold" data-testid="welcome-msg-preview">
                    LEX CARTER
                  </div>
                  <div className="inline-block mt-3 px-3 py-1 border border-[var(--phosphor)] text-[11px] tracking-[0.3em] phosphor-glow">
                    + 3 NEW TAPES
                  </div>
                </>
              )}
            </div>
            <div className="text-[8px] opacity-40 mt-1 tracking-[0.15em]">
              Sample values · real viewers see their own. Hero text scales up further on larger CRTs (text-4xl).
            </div>
          </div>

          {/* Token reference — each row is a click-to-insert button so the
              owner doesn't have to remember exact syntax. */}
          <div className="mb-3">
            <div className="text-[9px] opacity-60 tracking-[0.2em] mb-1">AVAILABLE TOKENS</div>
            <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {WELCOME_TOKENS.map((t) => (
                <button
                  key={t.token}
                  type="button"
                  onClick={() => setWelcomeSubtitleDraft((d) => (d + t.token).slice(0, 200))}
                  className="w-full text-left flex items-start gap-2 px-1.5 py-1 border border-transparent hover:border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)] transition-colors"
                  data-testid={`welcome-token-${t.token.replace(/[{}]/g, "")}`}
                >
                  <code className="text-[10px] text-[var(--phosphor)] phosphor-glow shrink-0 mt-0.5">{t.token}</code>
                  <span className="text-[9px] opacity-65 leading-snug">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={async () => {
                setSubmitting(true);
                try {
                  const ok = await onSaveWelcomeMessage({ subtitle: welcomeSubtitleDraft });
                  if (ok) setShowWelcomeMsg(false);
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              data-testid="welcome-msg-save-button"
              className="py-2 text-xs tracking-[0.2em] font-bold border-2 border-[var(--phosphor)] text-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
            >
              {submitting ? "..." : ">> SAVE <<"}
            </button>
            <button
              onClick={async () => {
                setSubmitting(true);
                try {
                  const ok = await onSaveWelcomeMessage({ subtitle: "" });
                  if (ok) setWelcomeSubtitleDraft("");
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              data-testid="welcome-msg-reset-button"
              className="py-2 text-xs tracking-[0.2em] border border-neutral-600 text-neutral-300 hover:border-[var(--phosphor)] hover:text-[var(--phosphor)] transition-colors disabled:opacity-40"
            >
              RESET
            </button>
          </div>
        </div>
      )}
      {showTrophyAdmin && isOwner && (
        <div
          className="absolute top-12 right-0 w-[440px] max-h-[80vh] overflow-y-auto bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 35%, transparent)]"
          data-testid="trophy-admin-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs tracking-[0.3em]">┌─ TROPHIES ─┐</div>
            <button onClick={() => setShowTrophyAdmin(false)} className="text-[var(--phosphor)]">
              <X size={14} />
            </button>
          </div>
          <div className="text-[10px] opacity-70 mb-3 leading-relaxed">
            Tick a trophy to keep it active. Untick to suppress it for every
            viewer (locked trophies stop firing AND disappear from the trophy
            room). Upload an .mp3 to play as the unlock confirmation sound
            (max 2&nbsp;MB).
          </div>

          {/* Sound uploader */}
          <div className="border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-2 mb-3">
            <div className="text-[10px] tracking-[0.25em] opacity-80 mb-1">UNLOCK SOUND</div>
            <div className="text-[11px] opacity-90 mb-2 truncate" data-testid="trophy-sound-state">
              {trophySettings && trophySettings.sound_url ? "● ACTIVE" : "○ NONE"}
            </div>
            <div className="flex items-center gap-2">
              <label
                className="flex-1 inline-flex items-center justify-center py-1 text-[11px] tracking-widest border border-[var(--phosphor)] text-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors cursor-pointer"
                data-testid="trophy-sound-upload"
              >
                {soundUploading ? "UPLOADING…" : "UPLOAD MP3"}
                <input
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/*"
                  hidden
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setSoundUploading(true);
                    try { await onUploadTrophySound(f); } finally { setSoundUploading(false); }
                    e.target.value = "";
                  }}
                />
              </label>
              {trophySettings && trophySettings.sound_url && (
                <button
                  type="button"
                  onClick={onClearTrophySound}
                  data-testid="trophy-sound-clear"
                  className="py-1 px-2 text-[11px] tracking-widest border border-neutral-600 text-neutral-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                >
                  CLEAR
                </button>
              )}
            </div>
          </div>

          {/* Per-trophy enable/disable list */}
          <div className="text-[10px] tracking-[0.25em] opacity-80 mb-1">CATALOG</div>
          <ul className="space-y-1 mb-3 max-h-[40vh] overflow-y-auto pr-1">
            {TROPHIES.map((t) => {
              const trophyOff = trophyDisabledDraft.includes(t.id);
              return (
                <li key={t.id} className="border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] px-2 py-1" data-testid={`trophy-admin-row-${t.id}`}>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!trophyOff}
                      onChange={(e) => {
                        setTrophyDisabledDraft((prev) => {
                          if (e.target.checked) return prev.filter((x) => x !== t.id);
                          return prev.includes(t.id) ? prev : [...prev, t.id];
                        });
                      }}
                      data-testid={`trophy-admin-toggle-${t.id}`}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] tracking-widest">{t.name}</div>
                      <div className="text-[10px] opacity-70 leading-snug">{t.desc}</div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>

          <button
            onClick={async () => {
              setSubmitting(true);
              try {
                const ok = await onSaveTrophySettings({ disabled: trophyDisabledDraft });
                if (ok) setShowTrophyAdmin(false);
              } finally {
                setSubmitting(false);
              }
            }}
            disabled={submitting}
            data-testid="trophy-admin-save"
            className="w-full py-2 text-xs tracking-[0.2em] font-bold border-2 border-[var(--phosphor)] text-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
          >
            {submitting ? "..." : ">> SAVE <<"}
          </button>
        </div>
      )}

      {/* ★ CATEGORIES — manage Sky Q rails (Movies, Music, Kids…) */}
      <OwnerCategoriesPanel
        open={showCategories && isOwner}
        onClose={() => setShowCategories(false)}
        getToken={getToken}
        onChanged={() => {
          // Broadcast so any open ChannelGuide re-fetches its lineup +
          // categories without forcing a page reload.
          window.dispatchEvent(new CustomEvent("crt:categories-updated"));
        }}
      />


      {/* ★ FEATURED manager — hero-spotlight rotation for the guide.
          Rendered outside OwnerBar to keep this file's line count
          manageable and to keep the form + list logic testable in
          isolation. Refetches server state whenever it opens. */}
      <OwnerFeaturedManager
        open={showFeatured && isOwner}
        onClose={() => setShowFeatured(false)}
        apiBase={apiBase}
        authHeaders={authHeaders && authHeaders()}
        channels={channels}
        videosByChannel={videosByChannel}
      />

      {/* ★ STATS panel — analytics modal, gated on owner auth same as
          the other menu rows. Polls /api/stats/live every 10s while
          open to keep the live counter current. */}
      <OwnerStats
        open={showStats && isOwner}
        onClose={() => setShowStats(false)}
        apiBase={apiBase}
        authHeaders={authHeaders && authHeaders()}
        channels={channels}
        videosByChannel={videosByChannel}
      />


      {/* ★ THEME picker — cabinet skin, phosphor colour and brand */}
      {showTheme && isOwner && (
        <div
          className="absolute top-12 right-0 w-[440px] max-h-[80vh] overflow-y-auto bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in_srgb,var(--phosphor)_35%,transparent)]"
          data-testid="theme-panel"
          style={{ fontFamily: "VT323, monospace" }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs tracking-[0.3em]">┌─ ★ THEME ─┐</div>
            <button
              onClick={() => setShowTheme(false)}
              className="text-[var(--phosphor)]"
              data-testid="theme-close"
            >
              <X size={14} />
            </button>
          </div>

          {/* Signature combos — one-click phosphor + cabinet presets.
              Brand text/callsign stay untouched so owner branding survives. */}
          <div className="mb-4">
            <div className="text-[10px] tracking-[0.25em] opacity-80 mb-1.5">★ SIGNATURE COMBOS</div>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { id: "default",     label: "SCARTLEAD",   sub: "GREEN · WOOD",      phosphor: "green", skin: "wood" },
                { id: "vault-95",    label: "VAULT 95",    sub: "AMBER · PEARL",     phosphor: "amber", skin: "pearl" },
                { id: "drive-in",    label: "DRIVE-IN",    sub: "GREEN · STEEL",     phosphor: "green", skin: "brushed-steel" },
                { id: "neon-arcade", label: "NEON ARCADE", sub: "BLUE · NEON",       phosphor: "blue",  skin: "neon" },
                { id: "vhs-night",   label: "VHS NIGHT",   sub: "AMBER · OBSIDIAN",  phosphor: "amber", skin: "obsidian" },
                { id: "focus",       label: "FOCUS",       sub: "WHITE · OBSIDIAN",  phosphor: "white", skin: "obsidian" },
              ].map((c) => {
                const active = themeDraft.phosphor === c.phosphor && themeDraft.skin === c.skin;
                return (
                  <button
                    key={c.id}
                    onClick={() => setThemeDraft((d) => ({ ...d, phosphor: c.phosphor, skin: c.skin }))}
                    data-testid={`theme-combo-${c.id}`}
                    className={`flex flex-col items-start p-1.5 border ${active ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_12%,transparent)]" : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] hover:border-[var(--phosphor)]"} transition-colors text-left`}
                  >
                    <span className="text-[10px] tracking-widest font-bold">{c.label}</span>
                    <span className="text-[8px] opacity-60 tracking-[0.18em]">{c.sub}</span>
                  </button>
                );
              })}
            </div>
            <div className="text-[9px] opacity-50 mt-1 text-center">— or fine-tune below —</div>
          </div>

          {/* Phosphor swatches */}
          <div className="mb-4">
            <div className="text-[10px] tracking-[0.25em] opacity-80 mb-1.5">PHOSPHOR COLOUR</div>
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { id: "green",  label: "GREEN",  swatch: "#39FF14" },
                { id: "amber",  label: "AMBER",  swatch: "#FFB000" },
                { id: "blue",   label: "BLUE",   swatch: "#5DD3FF" },
                { id: "white",  label: "WHITE",  swatch: "#E6E6E6" },
              ].map((opt) => {
                const active = themeDraft.phosphor === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setThemeDraft((d) => ({ ...d, phosphor: opt.id }))}
                    data-testid={`theme-phosphor-${opt.id}`}
                    className={`flex flex-col items-center gap-1 p-1.5 border ${active ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_12%,transparent)]" : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] hover:border-[var(--phosphor)]"} transition-colors`}
                  >
                    <span
                      className="w-6 h-6 rounded-full"
                      style={{ background: opt.swatch, boxShadow: `0 0 8px ${opt.swatch}` }}
                    />
                    <span className="text-[9px] tracking-widest">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Cabinet skin */}
          <div className="mb-4">
            <div className="text-[10px] tracking-[0.25em] opacity-80 mb-1.5">CABINET SKIN</div>
            <div className="grid grid-cols-5 gap-1.5">
              {[
                { id: "wood",           label: "WOOD",     swatch: "linear-gradient(135deg,#5a3d1a 0%,#2e1c11 100%)" },
                { id: "obsidian",       label: "BLACK",    swatch: "linear-gradient(135deg,#222 0%,#000 100%)" },
                { id: "pearl",          label: "PEARL",    swatch: "linear-gradient(135deg,#f5f0e3 0%,#d8d2c0 100%)" },
                { id: "brushed-steel",  label: "STEEL",    swatch: "linear-gradient(135deg,#7a7f87 0%,#3a3f47 100%)" },
                { id: "neon",           label: "NEON",     swatch: "linear-gradient(135deg,#ff00ff 0%,#00ffff 100%)" },
              ].map((opt) => {
                const active = themeDraft.skin === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setThemeDraft((d) => ({ ...d, skin: opt.id }))}
                    data-testid={`theme-skin-${opt.id}`}
                    className={`flex flex-col items-center gap-1 p-1 border ${active ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_12%,transparent)]" : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] hover:border-[var(--phosphor)]"} transition-colors`}
                  >
                    <span className="w-7 h-5 border border-black/40" style={{ background: opt.swatch }} />
                    <span className="text-[9px] tracking-widest">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* SEASON — site-wide festive decoration + palette shift.
              Manually toggled (no auto-schedule). Applies to every
              viewer immediately after APPLY THEME. `none` disables. */}
          <div className="mb-4">
            <div className="text-[10px] tracking-[0.25em] opacity-80 mb-1.5">SEASON</div>
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { id: "none",            label: "OFF",         glyph: "—" },
                { id: "christmas",       label: "CHRISTMAS",   glyph: "❄" },
                { id: "halloween",       label: "HALLOWEEN",   glyph: "🎃" },
                { id: "easter",          label: "EASTER",      glyph: "🥚" },
                { id: "valentines",      label: "VALENTINES",  glyph: "♥" },
                { id: "st-patrick",      label: "ST PATRICK",  glyph: "☘" },
                { id: "fourth-of-july",  label: "4TH OF JULY", glyph: "✦" },
              ].map((opt) => {
                const active = (themeDraft.active_season || "none") === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setThemeDraft((d) => ({ ...d, active_season: opt.id }))}
                    data-testid={`theme-season-${opt.id}`}
                    className={`flex flex-col items-center gap-0.5 p-1.5 border ${active ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_12%,transparent)]" : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] hover:border-[var(--phosphor)]"} transition-colors`}
                  >
                    <span className="text-lg leading-none">{opt.glyph}</span>
                    <span className="text-[8px] tracking-widest">{opt.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="text-[9px] opacity-60 mt-1 leading-snug">
              Site-wide festive overlay for every viewer. Manual toggle only — pick OFF when the season ends.
            </div>
          </div>

          {/* Brand text + callsign */}
          <div className="mb-4 grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] tracking-[0.25em] opacity-80 mb-1">BRAND TEXT</div>
              <input
                value={themeDraft.brand_text}
                onChange={(e) => setThemeDraft((d) => ({ ...d, brand_text: e.target.value.slice(0, 48) }))}
                maxLength={48}
                data-testid="theme-brand-text"
                className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] text-[var(--phosphor)] p-1.5 outline-none text-[11px] tracking-[0.18em]"
              />
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.25em] opacity-80 mb-1">CALL-SIGN</div>
              <input
                value={themeDraft.callsign}
                onChange={(e) => setThemeDraft((d) => ({ ...d, callsign: e.target.value.slice(0, 16) }))}
                maxLength={16}
                data-testid="theme-callsign"
                className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] text-[var(--phosphor)] p-1.5 outline-none text-[11px] tracking-[0.18em]"
              />
            </label>
          </div>

          {/* Live broadcast-bug overlay — owner-only branding strip
              rendered OVER the playing video without re-encoding. Toggle,
              text, style, corner, opacity. */}
          <div className="mb-4 border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-2.5">
            <label className="flex items-center gap-2 cursor-pointer select-none mb-2">
              <input
                type="checkbox"
                checked={!!themeDraft.live_overlay_enabled}
                onChange={(e) => setThemeDraft((d) => ({ ...d, live_overlay_enabled: e.target.checked }))}
                data-testid="theme-overlay-enabled"
                className="accent-[var(--phosphor)]"
              />
              <span className="text-[10px] tracking-[0.25em] opacity-90 font-bold">LIVE OVERLAY BUG</span>
              <span className="text-[9px] opacity-50 ml-auto">no re-encode</span>
            </label>

            <input
              value={themeDraft.live_overlay_text}
              onChange={(e) => setThemeDraft((d) => ({ ...d, live_overlay_text: e.target.value.slice(0, 48) }))}
              maxLength={48}
              placeholder="e.g. SCARTLEAD.TV"
              data-testid="theme-overlay-text"
              className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] text-[var(--phosphor)] p-1.5 outline-none text-[11px] tracking-[0.18em] mb-2"
            />

            <div className="grid grid-cols-3 gap-1 mb-2">
              {[
                { id: "minimal", label: "MINIMAL" },
                { id: "boxed",   label: "BOXED" },
                { id: "shadow",  label: "SHADOW" },
              ].map((s) => {
                const active = themeDraft.live_overlay_style === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setThemeDraft((d) => ({ ...d, live_overlay_style: s.id }))}
                    data-testid={`theme-overlay-style-${s.id}`}
                    className={`text-[9px] tracking-widest py-1 border ${active ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]" : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] hover:border-[var(--phosphor)]"} transition-colors`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <div className="text-[9px] tracking-[0.22em] opacity-65 mb-1">CORNER</div>
                <div className="grid grid-cols-2 gap-1">
                  {[
                    { id: "tl", label: "↖ TL" },
                    { id: "tr", label: "TR ↗" },
                    { id: "bl", label: "↙ BL" },
                    { id: "br", label: "BR ↘" },
                  ].map((p) => {
                    const active = themeDraft.live_overlay_position === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => setThemeDraft((d) => ({ ...d, live_overlay_position: p.id }))}
                        data-testid={`theme-overlay-pos-${p.id}`}
                        className={`text-[9px] tracking-widest py-1 border ${active ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]" : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] hover:border-[var(--phosphor)]"} transition-colors`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-[9px] tracking-[0.22em] opacity-65 mb-1">
                  <span>OPACITY</span>
                  <span className="tabular-nums opacity-90" data-testid="theme-overlay-opacity-value">{themeDraft.live_overlay_opacity}</span>
                </div>
                <input
                  type="range"
                  min={5}
                  max={100}
                  step={5}
                  value={themeDraft.live_overlay_opacity}
                  onChange={(e) => setThemeDraft((d) => ({ ...d, live_overlay_opacity: parseInt(e.target.value, 10) }))}
                  data-testid="theme-overlay-opacity"
                  className="w-full accent-[var(--phosphor)]"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => {
                setThemeDraft({
                  phosphor: "green", skin: "wood", room: "memphis",
                  brand_text: "SCARTLEAD.TV", callsign: "CRT-1986",
                  active_season: "none",
                });
              }}
              data-testid="theme-reset"
              className="flex-1 border border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] py-1.5 text-[10px] tracking-widest hover:bg-[color-mix(in_srgb,var(--phosphor)_8%,transparent)] transition-colors"
            >
              RESET DEFAULTS
            </button>
            <button
              onClick={async () => {
                if (themeSaving) return;
                setThemeSaving(true);
                try {
                  const token = typeof getToken === "function" ? getToken() : "";
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/site/theme`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(themeDraft),
                  });
                  if (!res.ok) throw new Error(await res.text());
                  const next = await res.json();
                  broadcastThemeUpdated(next);
                  setShowTheme(false);
                } catch (e) {
                  // Keep panel open so the owner can retry; surface error in title strip.
                  alert(`Theme save failed: ${e.message || e}`);
                } finally {
                  setThemeSaving(false);
                }
              }}
              disabled={themeSaving}
              data-testid="theme-save"
              className="flex-1 border border-[var(--phosphor)] py-1.5 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-50"
            >
              {themeSaving ? "SAVING…" : "APPLY THEME"}
            </button>
          </div>
        </div>
      )}



      {/* INBOX panel — list of submitted contact messages with mark-read /
          reply / delete affordances. Owned and fetched by the panel itself. */}
      {showInbox && isOwner && (
        <OwnerInboxPanel
          isOwner={isOwner}
          getToken={getToken}
          onClose={() => setShowInbox(false)}
          onChange={refreshInbox}
        />
      )}

      {/* AUDIO panel — station-wide loudness target slider + per-video
          loudnorm backfill. */}
      {showAudio && isOwner && (
        <OwnerAudioPanel
          isOwner={isOwner}
          getToken={getToken}
          onClose={() => setShowAudio(false)}
        />
      )}

      {/* TRIBUTE / IN MEMORIAM panel — one evolving doc, URL-only
          assets, publishes a 24h banner to every viewer. */}
      {showTribute && isOwner && (
        <TributePanel
          isOwner={isOwner}
          onClose={() => setShowTribute(false)}
          authHeaders={() => {
            const t = getToken && getToken();
            return t ? { Authorization: `Bearer ${t}` } : {};
          }}
          channels={channels}
        />
      )}

      {/* PLEDGE DRIVE panel — Buy Me A Coffee / tip-jar interstitial config.
          Owner sets URL, copy and frequency; a PREVIEW button dispatches
          `crt:pledge-drive-preview` so the ident fires immediately without
          having to wait for a random trigger. */}
      {showPledge && isOwner && (
        <div
          className="absolute top-12 right-0 w-[440px] max-h-[80vh] overflow-y-auto bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 35%, transparent)]"
          data-testid="pledge-drive-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs tracking-[0.3em]">┌─ PLEDGE DRIVE ─┐</div>
            <button onClick={() => setShowPledge(false)} className="text-[var(--phosphor)]">
              <X size={14} />
            </button>
          </div>
          <div className="text-[10px] opacity-70 mb-3 leading-snug">
            A short &ldquo;SUPPORT THE STATION&rdquo; ident that plays over the tube on
            some channel changes and links out to your Buy Me A Coffee (or any
            tip-jar URL). Fires with probability <span className="text-[var(--phosphor)]">1/FREQUENCY</span> per
            channel change &mdash; a frequency of 10 averages one pledge every 10 tape switches.
          </div>

          {/* Enable toggle */}
          <label className="flex items-center gap-2 mb-3 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={!!pledgeDraft.enabled}
              onChange={(e) => setPledgeDraft((d) => ({ ...d, enabled: e.target.checked }))}
              className="accent-[var(--phosphor)]"
              data-testid="pledge-enabled-input"
            />
            <span className="text-[11px] tracking-[0.2em]">ENABLE PLEDGE IDENT</span>
          </label>

          {/* URL */}
          <label className="block text-[10px] tracking-[0.2em] opacity-70 mb-1">TIP JAR URL</label>
          <input
            type="url"
            value={pledgeDraft.url}
            onChange={(e) => setPledgeDraft((d) => ({ ...d, url: e.target.value.slice(0, 400) }))}
            placeholder="https://buymeacoffee.com/your-handle"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] mb-3 placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono"
            data-testid="pledge-url-input"
          />

          {/* Frequency */}
          <label className="block text-[10px] tracking-[0.2em] opacity-70 mb-1">
            FREQUENCY — 1 IN <span className="text-[var(--phosphor)]">{pledgeDraft.frequency}</span> CHANNEL CHANGES
          </label>
          <input
            type="range"
            min={2}
            max={50}
            step={1}
            value={pledgeDraft.frequency}
            onChange={(e) => setPledgeDraft((d) => ({ ...d, frequency: Number(e.target.value) || 10 }))}
            className="w-full accent-[var(--phosphor)] mb-3"
            data-testid="pledge-frequency-input"
          />

          {/* Headline */}
          <label className="block text-[10px] tracking-[0.2em] opacity-70 mb-1">HEADLINE</label>
          <input
            type="text"
            value={pledgeDraft.headline}
            onChange={(e) => setPledgeDraft((d) => ({ ...d, headline: e.target.value.slice(0, 80) }))}
            placeholder="SUPPORT THE STATION"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] mb-3 placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono"
            data-testid="pledge-headline-input"
          />

          {/* Subline */}
          <label className="block text-[10px] tracking-[0.2em] opacity-70 mb-1">SUBLINE</label>
          <textarea
            value={pledgeDraft.subline}
            onChange={(e) => setPledgeDraft((d) => ({ ...d, subline: e.target.value.slice(0, 200) }))}
            rows={2}
            placeholder="This CRT is operator-funded. Tip a coffee to keep the tapes rolling."
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] mb-3 placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono"
            data-testid="pledge-subline-input"
          />

          {/* CTA label */}
          <label className="block text-[10px] tracking-[0.2em] opacity-70 mb-1">CTA BUTTON LABEL</label>
          <input
            type="text"
            value={pledgeDraft.cta_label}
            onChange={(e) => setPledgeDraft((d) => ({ ...d, cta_label: e.target.value.slice(0, 32) }))}
            placeholder="BUY ME A COFFEE"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-[12px] mb-4 placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono"
            data-testid="pledge-cta-input"
          />

          <div className="grid grid-cols-3 gap-2">
            <button
              disabled={pledgeSaving}
              onClick={async () => {
                setPledgeSaving(true);
                try {
                  const ok = await (onSavePledgeDrive
                    ? onSavePledgeDrive({
                        enabled: !!pledgeDraft.enabled,
                        url: pledgeDraft.url,
                        headline: pledgeDraft.headline,
                        subline: pledgeDraft.subline,
                        cta_label: pledgeDraft.cta_label,
                        frequency: pledgeDraft.frequency,
                      })
                    : false);
                  if (ok) setShowPledge(false);
                } finally { setPledgeSaving(false); }
              }}
              className="py-2 text-xs tracking-[0.2em] bg-[var(--phosphor)] text-black hover:brightness-110 transition disabled:opacity-40 font-bold"
              data-testid="pledge-save-button"
            >
              {pledgeSaving ? "SAVING…" : "SAVE"}
            </button>
            <button
              onClick={() => {
                // Dispatch preview even if unsaved — uses the live draft
                // so the operator can iterate on copy without leaving.
                window.dispatchEvent(new CustomEvent("crt:pledge-drive-preview"));
              }}
              className="py-2 text-xs tracking-[0.2em] border border-[var(--phosphor)] text-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_10%,transparent)] transition-colors"
              data-testid="pledge-preview-button"
            >
              PREVIEW
            </button>
            <button
              onClick={() => setPledgeDraft({
                enabled: false, url: "", headline: "", subline: "", cta_label: "", frequency: 10,
              })}
              className="py-2 text-xs tracking-[0.2em] border border-neutral-600 text-neutral-300 hover:border-[var(--phosphor)] hover:text-[var(--phosphor)] transition-colors"
              data-testid="pledge-reset-button"
            >
              RESET
            </button>
          </div>
          {/* Bonus preview — ★ THANK YOU ★ bumper. Fires organically
              when a viewer returns to the tab after clicking the CTA
              (15s+ away). Handy for verifying the golden ident + the
              PATRON trophy toast without an actual test-tip. */}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("crt:thanks-preview"))}
            className="w-full mt-2 py-2 text-xs tracking-[0.2em] border rounded-md transition-colors"
            style={{
              borderColor: "#f5c542",
              color: "#f5c542",
              backgroundColor: "rgba(245, 197, 66, 0.05)",
            }}
            data-testid="thanks-preview-button"
          >
            ★ PREVIEW THANK-YOU BUMPER ★
          </button>
          <div className="text-[9px] opacity-50 mt-3 leading-snug">
            PREVIEW uses your unsaved draft so you can iterate before hitting SAVE.
            The ident always fades back automatically after ~4 seconds.
            The gold ★ THANK YOU ★ card + PATRON trophy fires once per viewer
            when they return to the tab 15s+ after clicking the tip button.
          </div>
        </div>
      )}



      {/* Reveal key panel */}
      {showKey && isOwner && (
        <div
          className="absolute top-12 right-0 w-[360px] bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 30%, transparent)]"
          data-testid="owner-key-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs tracking-[0.3em]">┌─ OWNER KEY ─┐</div>
            <button onClick={() => setShowKey(false)} className="text-[var(--phosphor)]">
              <X size={14} />
            </button>
          </div>
          <div className="text-[10px] opacity-80 mb-2">
            BACK THIS UP. Paste it on any other device to regain broadcaster access.
          </div>
          <div className="bg-black border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] p-2 text-[11px] break-all select-all">
            {getToken()}
          </div>
        </div>
      )}

      {/* Restore panel */}
      {showRestore && (
        <div
          className="absolute top-12 right-0 w-[360px] bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 30%, transparent)]"
          data-testid="restore-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs tracking-[0.3em]">┌─ RESTORE OWNER ─┐</div>
            <button onClick={() => setShowRestore(false)} className="text-[var(--phosphor)]">
              <X size={14} />
            </button>
          </div>
          <div className="text-[10px] opacity-80 mb-2">
            Paste your saved owner key to unlock broadcaster mode on this device.
          </div>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="paste owner key..."
            data-testid="owner-token-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none text-xs mb-2"
          />
          <button
            onClick={async () => {
              const ok = await onRestore(tokenInput.trim());
              if (ok) {
                setShowRestore(false);
                setTokenInput("");
              }
            }}
            data-testid="restore-confirm"
            className="w-full border border-[var(--phosphor)] py-1.5 text-xs hover:bg-[var(--phosphor)] hover:text-black transition-colors"
          >
            UNLOCK
          </button>
        </div>
      )}

      {!isOwner && (
        <div className="hidden md:flex items-center gap-1 text-[10px] text-amber-400/80 ml-2">
          <Eye size={12} /> WATCH-ONLY BROADCAST
        </div>
      )}
    </div>
  );
}
